import React, { FC } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Tooltip,
} from "@mui/material";
import { ArrowBack, ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DoubleMaterialityQuestionTimeHorizon } from "@green0meter-back/doubleMateriality/enums/DoubleMaterialityQuestion.enums";
import type { QuestionTextTranslations } from "@green0meter-back/doubleMateriality/dtos/types/QuestionAnswerRow.type";
import { DoubleMaterialityIroTypeEnum } from "@green0meter/api-client";

import { useGetQuestionAnswers } from "@api/hooks/doubleMateriality/GetQuestionAnswers/useGetQuestionAnswers";
import { useGetStakeholders, useGetAssessments } from "@api";
import { useUserStore } from "@store/user";
import { getYearFromSearchParams } from "@helpers";

interface QuestionAnswerWithRelations {
  questionId?: string;
  stakeholderId?: string;
  answeredAt?: string | Date | null;
  textAnswer?: string | null;
  externalAnswer?: number | null;
  isReal?: boolean | null;
  isPositiveImpact?: boolean | null;
  scope?: number | null;
  levelOfSeverity?: number | null;
  intensity?: number | null;
  range?: number | null;
  remediability?: number | null;
  probability?: number | null;
  timeHorizon?: DoubleMaterialityQuestionTimeHorizon[] | null;
  question?: {
    sustainabilityMatter?: string;
    questions?: QuestionTextTranslations | string | null;
    assessment?: {
      sustainabilityMatter?: string;
    };
    iro?: DoubleMaterialityIroTypeEnum;
  };
}

const DoubleMaterialityAnswersPage: FC = () => {
  const { t } = useTranslation();
  const { stakeholderId } = useParams<{ stakeholderId: string }>();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);
  const step = searchParams.get("step") || "4";

  const { answers } = useGetQuestionAnswers(user);
  const { stakeholders } = useGetStakeholders(user, {
    pageNumber: 1,
    pageSize: 1000,
    year,
  });
  const { assessments } = useGetAssessments(user, {
    year,
    pageNumber: 1,
    pageSize: 1000,
  });

  const stakeholdersList = stakeholders?.data ?? [];
  const answersList = (answers ?? []) as QuestionAnswerWithRelations[];

  // Filter stakeholders with assigned questions
  const stakeholderStats = new Map<string, { total: number; answered: number }>();

  answersList.forEach((answer) => {
    const sid = answer.stakeholderId;

    if (!sid) return;
    const stats = stakeholderStats.get(sid) ?? { total: 0, answered: 0 };

    stats.total += 1;
    if (answer.answeredAt) stats.answered += 1;
    stakeholderStats.set(sid, stats);
  });

  const stakeholdersWithAssignedQuestions = stakeholdersList.filter((s) =>
    stakeholderStats.has(s.id),
  );

  // Find the stakeholder
  const stakeholder = stakeholdersWithAssignedQuestions.find(
    (s) => s.id === stakeholderId,
  );
  const currentIndex = stakeholdersWithAssignedQuestions.findIndex(
    (s) => s.id === stakeholderId,
  );
  const previousStakeholder =
    currentIndex > 0 ? stakeholdersWithAssignedQuestions[currentIndex - 1] : null;
  const nextStakeholder =
    currentIndex < stakeholdersWithAssignedQuestions.length - 1
      ? stakeholdersWithAssignedQuestions[currentIndex + 1]
      : null;

  // Filter answers for this stakeholder
  const stakeholderAnswers = answersList.filter(
    (answer) => answer.stakeholderId === stakeholderId,
  );

  const handleBack = (): void => {
    navigate(`/csrd/double-materiality?step=${step}&year=${year}`);
  };

  const handlePreviousStakeholder = (): void => {
    if (previousStakeholder) {
      navigate(
        `/csrd/double-materiality/answers/${previousStakeholder.id}?year=${year}&step=${step}`,
      );
    }
  };

  const handleNextStakeholder = (): void => {
    if (nextStakeholder) {
      navigate(
        `/csrd/double-materiality/answers/${nextStakeholder.id}?year=${year}&step=${step}`,
      );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h5">
                {t("doubleMateriality.answers", "Answers")}
              </Typography>
            </Box>
          </Stack>

          {/* Navigation controls */}
          {stakeholder && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "primary.main",
                    fontSize: "1rem",
                    fontWeight: "bold",
                  }}
                >
                  {stakeholder.fullName.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="body1" fontWeight="medium">
                  {stakeholder.fullName}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5}>
                <Tooltip
                  title={
                    previousStakeholder
                      ? t(
                        "doubleMateriality.previousStakeholder",
                        "Previous stakeholder",
                      )
                      : ""
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handlePreviousStakeholder}
                      disabled={!previousStakeholder}
                    >
                      <ArrowBackIos fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    nextStakeholder
                      ? t("doubleMateriality.nextStakeholder", "Next stakeholder")
                      : ""
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleNextStakeholder}
                      disabled={!nextStakeholder}
                    >
                      <ArrowForwardIos fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          )}
        </Stack>

        {/* Answers Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t(
                    "doubleMateriality.sustainabilityMatter",
                    "Sustainability matter",
                  )}
                </TableCell>
                <TableCell>{t("doubleMateriality.results.iro", "IRO")}</TableCell>
                <TableCell>
                  {t("doubleMateriality.results.question", "Question")}
                </TableCell>
                <TableCell>
                  {t("doubleMateriality.results.external", "External")}
                </TableCell>
                <TableCell>
                  {t("doubleMateriality.qualitativeAnswer", "[Qualitative answer]")}
                </TableCell>
                <TableCell>
                  {t("doubleMateriality.realPotential", "Real / Potential")}
                </TableCell>
                <TableCell>
                  {t(
                    "doubleMateriality.results.negativePositive",
                    "Negative/Positive",
                  )}
                </TableCell>
                <TableCell>
                  {t("doubleMateriality.levelOfSeverity", "Level of severity (1~5)")}
                </TableCell>
                <TableCell>
                  {t("doubleMateriality.intensity", "Intensity (1~5)")}
                </TableCell>
                <TableCell>{t("doubleMateriality.range", "Range (1~5)")}</TableCell>
                <TableCell>
                  {t("doubleMateriality.remediability", "Remediability (1~5)")}
                </TableCell>
                <TableCell>
                  {t("doubleMateriality.probability", "Probability (-3~3)")}
                </TableCell>
                <TableCell>
                  {t("doubleMateriality.results.timeHorizon", "Time horizon")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stakeholderAnswers.map((answer, index: number) => {
                const sustainabilityMatter =
                  answer.question?.assessment?.sustainabilityMatter ||
                  answer.question?.sustainabilityMatter ||
                  "-";

                const assessment = assessments?.data?.find(
                  (a) => a.sustainabilityMatter === sustainabilityMatter,
                );

                const hasIro = (iro: DoubleMaterialityIroTypeEnum): boolean => {
                  return answer.question?.iro === iro;
                };
                const iroParts = [];

                if (hasIro(DoubleMaterialityIroTypeEnum.Impact))
                  iroParts.push("Impact");
                if (hasIro(DoubleMaterialityIroTypeEnum.Risk)) iroParts.push("Risk");
                if (hasIro(DoubleMaterialityIroTypeEnum.Opportunity))
                  iroParts.push("Opportunity");
                const iro = iroParts.join(", ");

                const questionValue = (() => {
                  const questions = answer.question?.questions;

                  if (!questions) return "";
                  if (typeof questions === "string") return questions;
                  const translations = questions as QuestionTextTranslations;

                  return (
                    translations.en ??
                    Object.values(translations).find((value) => value) ??
                    ""
                  );
                })();

                const isPositive = answer.isPositiveImpact;
                const negativePositive =
                  isPositive === null ? "" : isPositive ? "Positive" : "Negative";

                return (
                  <TableRow key={index}>
                    <TableCell>{sustainabilityMatter}</TableCell>
                    <TableCell>{iro}</TableCell>
                    <TableCell>
                      <div dangerouslySetInnerHTML={{ __html: questionValue }}></div>
                    </TableCell>
                    <TableCell>{answer.externalAnswer ?? "-"}</TableCell>
                    <TableCell
                      dangerouslySetInnerHTML={{ __html: answer.textAnswer || "-" }}
                    ></TableCell>
                    <TableCell>
                      {answer.isReal === null
                        ? "-"
                        : answer.isReal
                          ? t("doubleMateriality.real", "Real")
                          : t("doubleMateriality.potential", "Potential")}
                    </TableCell>
                    <TableCell>{negativePositive}</TableCell>
                    <TableCell>{answer.levelOfSeverity ?? "-"}</TableCell>
                    <TableCell>{answer.intensity ?? "-"}</TableCell>
                    <TableCell>{answer.range ?? "-"}</TableCell>
                    <TableCell>{answer.remediability ?? "-"}</TableCell>
                    <TableCell>{answer.probability ?? "-"}</TableCell>
                    <TableCell>
                      {answer.timeHorizon
                        ?.map((h) => DoubleMaterialityQuestionTimeHorizon[h])
                        .join(", ") ?? "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {stakeholderAnswers.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t("doubleMateriality.noAnswersYet", "No answers yet")}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default DoubleMaterialityAnswersPage;
